import React, { useEffect, useState } from 'react';
import { Helmet } from '@plone/volto/helpers';
import { fetchApi, getApiPath } from '../../api';
import './CommunityComparison.css';

// ============================================================
// Tab 1: English Corrections (unchanged)
// ============================================================
const EnglishCorrectionsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPlatform) params.append('platform', filterPlatform);
      if (filterStatus) params.append('status', filterStatus);
      if (search) params.append('search', search);
      const qs = params.toString();
      const data = await fetchApi(
        `${getApiPath()}/@community/corrections${qs ? '?' + qs : ''}`,
      );
      setItems(data.items || []);
      setPlatforms(data.platforms || []);
      setStatuses(data.statuses || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterPlatform, filterStatus, search]);

  if (loading) return <div className="community-loading">Loading English corrections...</div>;

  return (
    <div>
      {error && <div className="community-error">{error}</div>}

      <div className="community-filters">
        <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
          <option value="">All platforms</option>
          {platforms.map((p) => (
            <option key={p.platform} value={p.platform}>{p.platform} ({p.cnt})</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s.status} value={s.status}>{s.status} ({s.cnt})</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
          onBlur={() => setSearch(searchInput)}
        />
        <span className="filter-count">{items.length} corrections</span>
      </div>

      <table className="community-table">
        <thead>
          <tr>
            <th style={{ width: '70px' }}>Platform</th>
            <th style={{ width: '75px' }}>Source</th>
            <th>Original English</th>
            <th>Corrected English</th>
            <th style={{ width: '80px' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <React.Fragment key={`${item.message_id}-${item.platform}`}>
              <tr
                className="clickable"
                onClick={() => setExpandedId(expandedId === item.message_id ? null : item.message_id)}
              >
                <td><span className={`platform-label ${item.platform}`}>{item.platform}</span></td>
                <td>
                  <span
                    className={`flag-label ${item.has_default_comment ? 'default' : 'msgid'}`}
                    title={item.has_default_comment
                      ? 'English source from #. Default comment in .pot file'
                      : 'No #. Default — English source is the msgid itself'}
                  >
                    {item.has_default_comment ? '#. Default' : 'msgid'}
                  </span>
                </td>
                <td className="cell-original">{item.original_default}</td>
                <td className="cell-corrected">{item.corrected_default}</td>
                <td><span className={`status-label ${item.status}`}>{item.status}</span></td>
              </tr>
              {expandedId === item.message_id && (
                <tr>
                  <td colSpan="5">
                    <div className="expanded-detail">
                      <p><strong>msgid:</strong> <code className="msgid">{item.msgid}</code></p>
                      {item.has_default_comment && item.default_text && (
                        <p><strong>#. Default:</strong> <code className="msgid">{item.default_text}</code></p>
                      )}
                      <p><strong>Domain:</strong> {item.domain} | <strong>Platform:</strong> {item.platform}</p>
                      {item.notes && <p><strong>Correction notes:</strong> {item.notes}</p>}
                      <div className="side-by-side">
                        <div>
                          <p><strong>Original:</strong></p>
                          <div className="code-box original">{item.original_default || '(none)'}</div>
                        </div>
                        <div>
                          <p><strong>Corrected:</strong></p>
                          <div className="code-box corrected">{item.corrected_default}</div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {items.length === 0 && (
            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No corrections match the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================
// Status dot component
// ============================================================
const StatusDot = ({ match }) => {
  if (!match) return null;
  const colors = {
    matches: '#21ba45',
    differs: '#f2711c',
    ai_warned: '#fbbd08',
    ai_rejected: '#db2828',
    pending: '#999',
  };
  const titles = {
    matches: 'Applied — live in production',
    differs: 'Applied but since changed',
    ai_warned: 'AI flagged — needs review',
    ai_rejected: 'AI rejected',
    pending: 'Pending review',
  };
  return (
    <span
      className="status-dot"
      style={{ background: colors[match] || '#999' }}
      title={titles[match] || match}
    />
  );
};

// ============================================================
// Tab 2: Translation Comparison (DR-2026-072 redesign)
// ============================================================
const TranslationComparisonTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [contributors, setContributors] = useState([]);
  const [selectedContributor, setSelectedContributor] = useState('');
  const [contributorFilter, setContributorFilter] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterDifferent, setFilterDifferent] = useState(false);
  const [showOriginalEnglish, setShowOriginalEnglish] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [langLoading, setLangLoading] = useState(true);

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const data = await fetchApi(`${getApiPath()}/@community/languages`);
        setLanguages(data.items || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLangLoading(false);
      }
    };
    loadLanguages();
  }, []);

  useEffect(() => {
    if (!selectedLang) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ language: selectedLang });
        if (filterPlatform) params.append('platform', filterPlatform);
        if (filterDifferent) params.append('filter', 'different');
        if (search) params.append('search', search);
        if (selectedContributor) params.append('contributor_id', selectedContributor);
        if (contributorFilter) params.append('contributor_filter', contributorFilter);
        const data = await fetchApi(
          `${getApiPath()}/@community/translations?${params}`,
        );
        setItems(data.items || []);
        setContributors(data.contributors || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedLang, filterPlatform, filterDifferent, search, selectedContributor, contributorFilter]);

  // Reset contributor when language changes
  useEffect(() => {
    setSelectedContributor('');
    setContributorFilter('');
    setContributors([]);
  }, [selectedLang]);

  if (langLoading) return <div className="community-loading">Loading languages...</div>;

  const hasContributor = !!selectedContributor;
  const selectedContributorName = contributors.find(
    (c) => String(c.id) === selectedContributor,
  )?.fullname;

  return (
    <div>
      {error && <div className="community-error">{error}</div>}

      <div className="community-filters">
        <select
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
        >
          <option value="">Select language...</option>
          {languages.map((l) => (
            <option key={l.language} value={l.language}>
              {l.language}{l.language_name_native ? ' — ' + l.language_name_native : ''} ({l.translation_count})
            </option>
          ))}
        </select>
        {selectedLang && (
          <>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
            >
              <option value="">All platforms</option>
              {['nick', 'volto', 'volto_slate', 'volto_vlt', 'plone'].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {contributors.length > 0 && (
              <select
                value={selectedContributor}
                onChange={(e) => { setSelectedContributor(e.target.value); setContributorFilter(''); }}
              >
                <option value="">All translations</option>
                {contributors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.fullname} ({c.total})
                  </option>
                ))}
              </select>
            )}
            {hasContributor && (
              <select
                value={contributorFilter}
                onChange={(e) => setContributorFilter(e.target.value)}
              >
                <option value="">All ({contributors.find((c) => String(c.id) === selectedContributor)?.total || 0})</option>
                <option value="applied">
                  Applied ({contributors.find((c) => String(c.id) === selectedContributor)?.applied_matches || 0})
                </option>
                <option value="differs">
                  Differs ({contributors.find((c) => String(c.id) === selectedContributor)?.applied_differs || 0})
                </option>
                <option value="pending">
                  Pending review ({contributors.find((c) => String(c.id) === selectedContributor)?.pending_review || 0})
                </option>
              </select>
            )}
            {!hasContributor && (
              <>
                <button
                  className={`toggle-btn ${filterDifferent ? 'active' : ''}`}
                  onClick={() => setFilterDifferent(!filterDifferent)}
                >
                  {filterDifferent ? 'Showing changed only' : 'Show changed only'}
                </button>
                <button
                  className={`toggle-btn ${showOriginalEnglish ? 'active' : ''}`}
                  onClick={() => setShowOriginalEnglish(!showOriginalEnglish)}
                >
                  {showOriginalEnglish ? 'Showing original English' : 'Show original English'}
                </button>
              </>
            )}
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
              onBlur={() => setSearch(searchInput)}
            />
          </>
        )}
        <span className="filter-count">
          {selectedLang ? `${items.length} translations` : `${languages.length} languages`}
        </span>
      </div>

      {!selectedLang ? (
        <div className="info-box">
          <strong>Select a language</strong> from the dropdown to see how official .po translations compare to our versions.
          {contributors.length > 0 && ' Then select a contributor to see their individual work.'}
        </div>
      ) : loading ? (
        <div className="community-loading">Loading {selectedLang} translations...</div>
      ) : (
        <table className="community-table">
          <thead>
            <tr>
              <th style={{ width: '70px' }}>Platform</th>
              {hasContributor && <th style={{ width: '30px' }}></th>}
              <th style={{ width: '25%' }}>{showOriginalEnglish ? 'Original English' : 'English'}</th>
              <th>Current Translation</th>
              {!hasContributor && <th>Community .po</th>}
              {!hasContributor && <th style={{ width: '50px' }}>Source</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <React.Fragment key={`${item.message_id}-${idx}`}>
                <tr
                  className="clickable"
                  onClick={() => setExpandedId(expandedId === idx ? null : idx)}
                >
                  <td><span className={`platform-label ${item.platform}`}>{item.platform}</span></td>
                  {hasContributor && (
                    <td style={{ textAlign: 'center' }}>
                      <StatusDot match={item.contributor_match} />
                    </td>
                  )}
                  <td style={{ fontSize: '0.9em', whiteSpace: 'pre-wrap' }}>
                    {showOriginalEnglish
                      ? (item.original_english || item.english_source)
                      : item.english_source}
                  </td>
                  <td className="cell-corrected">
                    {item.our_translation}
                  </td>
                  {!hasContributor && (
                    <td className={`cell-po ${!item.po_value ? 'empty' : ''}`}>
                      {item.po_value || <span className="cell-not-translated">(not translated)</span>}
                    </td>
                  )}
                  {!hasContributor && (
                    <td>
                      <span className={`source-label ${
                        item.translation_source === 'ai_generated' ? 'ai' :
                        item.translation_source === 'copied' ? 'po' :
                        item.translation_source === 'corrected' ? 'fixed' : 'other'
                      }`}>
                        {item.translation_source === 'ai_generated' ? 'AI' :
                         item.translation_source === 'copied' ? '.po' :
                         item.translation_source === 'corrected' ? 'fixed' :
                         item.translation_source}
                      </span>
                    </td>
                  )}
                </tr>
                {expandedId === idx && (
                  <tr>
                    <td colSpan={hasContributor ? 4 : 5}>
                      <div className="expanded-detail">
                        {item.original_english && item.original_english !== item.corrected_english ? (
                          <div className="side-by-side" style={{ marginBottom: '0.8em' }}>
                            <div>
                              <p><strong>Original English:</strong></p>
                              <div className="code-box original">{item.original_english}</div>
                            </div>
                            <div>
                              <p><strong>Corrected English:</strong></p>
                              <div className="code-box corrected">{item.corrected_english}</div>
                            </div>
                          </div>
                        ) : (
                          <p><strong>English:</strong> {item.english_source}</p>
                        )}
                        <div className="side-by-side">
                          <div>
                            <p><strong>Community .po:</strong></p>
                            <div className="code-box original">
                              {item.po_value || '(not translated in community .po)'}
                            </div>
                          </div>
                          <div>
                            <p><strong>Current translation:</strong></p>
                            <div className="code-box corrected">
                              {item.our_translation}
                            </div>
                          </div>
                        </div>
                        {item.contributor_value && (
                          <div style={{ marginTop: '0.8em' }}>
                            <p>
                              <strong>{item.contributor_name || 'Contributor'}:</strong>
                              {' '}
                              <span className={`status-label ${item.contributor_match}`}>
                                {item.contributor_match === 'matches' ? 'applied — live'
                                  : item.contributor_match === 'differs' ? 'applied — since changed'
                                  : item.contributor_status}
                              </span>
                            </p>
                            <div className={`code-box ${item.contributor_match === 'matches' ? 'corrected' : 'original'}`}>
                              {item.contributor_value}
                            </div>
                          </div>
                        )}
                        <p className="meta">Source: {item.translation_source}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={hasContributor ? 4 : 5} style={{ textAlign: 'center', padding: '2em', color: '#999' }}>
                {hasContributor
                  ? 'No contributions match the current filter.'
                  : 'No translations found for this language and filter.'}
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ============================================================
// Main component
// ============================================================
const CommunityComparison = () => {
  const [activeTab, setActiveTab] = useState('english');

  return (
    <div className="community-page">
      <Helmet title="Community Translation Review" />

      <h1>
        Community Translation Review
        <small>Compare original Plone .pot/.po text with corrected versions. Open to the community — no login required.</small>
      </h1>

      <div className="info-box">
        This project imported all Plone .pot and .po files into a structured database, then
        systematically reviewed and corrected them. The <strong>English Corrections</strong> tab
        shows where the original English source text was improved. The <strong>Translation
        Comparison</strong> tab shows how official community .po translations compare to our
        versions in any language — and when a contributor is selected, their individual work.
      </div>

      <div className="community-tabs">
        <button
          className={activeTab === 'english' ? 'active' : ''}
          onClick={() => setActiveTab('english')}
        >
          English Corrections
        </button>
        <button
          className={activeTab === 'translations' ? 'active' : ''}
          onClick={() => setActiveTab('translations')}
        >
          Translation Comparison
        </button>
      </div>

      {activeTab === 'english' ? <EnglishCorrectionsTab /> : <TranslationComparisonTab />}
    </div>
  );
};

export default CommunityComparison;
